import { ReactComponent as RiskIcon } from '@assets/svg/icon-risk-white-orange.svg';
import { WCS } from '@components/ui/calendar/calendar-week/week-utils';
import { ICalendarEvent } from '@components/ui/calendar/calendars';
import { actions } from '@store/ducks/e-visits';
import { addMinutes, format } from 'date-fns';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TExistingEVisitRule } from 'types/e-visits';
import TStore from 'types/store';
import { getTagColors } from '../../../../e-visits/e-visits.util';
import { fitAllBlocksInInterval } from '../../../../utils';
import { EditItemSC } from './edit-item.styled';

interface EditItemProps {
  item: ICalendarEvent<TExistingEVisitRule | undefined>;
}

const EditItem: React.FunctionComponent<EditItemProps> = ({ item }) => {
  const start = new Date(item.start);
  const end = new Date(item.end);
  const blockSize = item.eventData?.blockSize || 15;

  const { fullBlocks, emptySpace } = fitAllBlocksInInterval(start, end, blockSize);
  const height = item.eventData?.blockSize
    ? item.eventData?.blockSize * WCS.minuteHeight
    : undefined;

  const ranges = [...new Array(fullBlocks).keys()].map((k) => {
    const s = addMinutes(start, k * blockSize);
    const e = addMinutes(s, blockSize);
    return { s, e };
  });

  const { selectedTag } = useSelector((state: TStore) => ({
    selectedTag: state.eVisits.selectedTag,
  }));

  const dispatch = useDispatch();
  useEffect(() => {
    return () => {
      dispatch(actions.clearTag());
    };
  }, [dispatch]);

  const { backgroundColor, textColor } = useMemo(() => {
    if (selectedTag) {
      return getTagColors(selectedTag.color);
    } else if (item.eventData?.tag) {
      return getTagColors(item.eventData.tag.color);
    }

    return {
      backgroundColor: '#62ab81',
      textColor: '#62ab81',
    };
  }, [selectedTag, item]);

  return (
    <EditItemSC backgroundColor={backgroundColor} textColor={textColor}>
      {ranges.map(({ s, e }) => (
        <div
          className="item-wrapper"
          key={`${s}-${e}`}
          style={{
            maxHeight: height,
            minHeight: height ? height - 1 : undefined,
          }}
        >
          <div className="item">
            {format(s, 'HH:mm')} - {format(e, 'HH:mm')}
          </div>
        </div>
      ))}
      {emptySpace ? (
        <div className="empty-space">
          <RiskIcon />
        </div>
      ) : null}
    </EditItemSC>
  );
};

export default React.memo(EditItem);
